import { ValidationResult, FrontmatterMetadata } from '../types.js';
import { load } from 'js-yaml';

export class FileFormatValidator {
  // More permissive regex that allows for extra spaces and newlines
  private readonly FRONTMATTER_REGEX = /^-{3,}[\r\n\s]+([\s\S]*?)[\r\n\s]+-{3,}[\r\n\s]*([\s\S]*)$/;

  /**
   * Validates that a file has the correct format:
   * - Must be a markdown file
   * - Must have frontmatter section that can be parsed as YAML
   * - Must have some content (even if just whitespace)
   */
  validateFileFormat(filePath: string, content: string): ValidationResult {
    const errors: string[] = [];

    // Check file extension - case insensitive
    if (!filePath.toLowerCase().endsWith('.md')) {
      errors.push('File must be a markdown file');
      return { valid: false, errors, warnings: [] };
    }

    // Check frontmatter structure - more permissive now
    const match = content.match(this.FRONTMATTER_REGEX);
    if (!match) {
      errors.push('File must have frontmatter section marked with ---');
      return { valid: false, errors, warnings: [] };
    }

    // Get frontmatter content, allowing for whitespace
    const frontmatter = match[1].trim();
    
    // Validate YAML - only care if it parses
    try {
      const parsed = load(frontmatter);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        errors.push('Frontmatter must be a valid YAML object');
        return { valid: false, errors, warnings: [] };
      }
    } catch (err) {
      errors.push('Frontmatter must be valid YAML');
      return { valid: false, errors, warnings: [] };
    }

    return {
      valid: true,
      errors,
      warnings: []
    };
  }

  /**
   * Parse frontmatter from content string
   * Returns null if no valid frontmatter found
   */
  parseFrontmatter(content: string): Record<string, any> | null {
    const match = content.match(this.FRONTMATTER_REGEX);
    if (!match) {
      return null;
    }

    try {
      const frontmatter = match[1].trim();
      const parsed = load(frontmatter);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }
      return parsed as Record<string, any>;
    } catch (err) {
      return null;
    }
  }

  validateFrontmatterFormat(content: string): ValidationResult {
    // Check for empty content
    if (!content.trim()) {
      return {
        valid: false,
        errors: ['Content is empty'],
        warnings: []
      };
    }

    // Check for frontmatter delimiters
    const match = content.match(/^---\n([\s\S]*?\n)---\n/);
    if (!match) {
      return {
        valid: false,
        errors: ['Content must start with frontmatter delimiters (---)'],
        warnings: []
      };
    }

    try {
      // Parse YAML content
      const frontmatter = load(match[1]);
      if (typeof frontmatter !== 'object' || frontmatter === null) {
        return {
          valid: false,
          errors: ['Frontmatter must be an object'],
          warnings: []
        };
      }
    } catch (error: any) {
      return {
        valid: false,
        errors: [`Failed to parse frontmatter: ${error.message}`],
        warnings: []
      };
    }

    return {
      valid: true,
      errors: [],
      warnings: []
    };
  }
} 